import { Trans } from "@lingui/macro";
import { Check } from "@winamp/icons/Check.js";
import { Rewards } from "@winamp/icons/Rewards.js";
import { Undo } from "@winamp/icons/Undo.js";
import { Button } from "@winamp/ui/components/Button/Button.js";
import { Tooltip } from "@winamp/ui/components/Tooltip/Tooltip.js";
import { TooltipContent } from "@winamp/ui/components/Tooltip/TooltipContent.js";
import { TooltipTrigger } from "@winamp/ui/components/Tooltip/TooltipTrigger.js";
import { classNames } from "@winamp/ui/utils/classNames.js";
import { forwardRef, useEffect, useState } from "react";
import { useClickOutside } from "~/hooks/use-click-outside";
import { useFetcherWithReset } from "~/hooks/use-fetcher-with-reset";
import { useContextPath } from "~/modules/routing/use-context-path";
import type { loader } from "~/routes/resources+/app+/title-beautifier+/route";

interface ITitleBeautifierProps {
  fetcherKey: string;
  onChange: (title: string) => void;
  value: string | undefined;
  disabled?: boolean;
  className?: string;
}

export const TitleBeautifier = forwardRef<
  HTMLButtonElement,
  ITitleBeautifierProps
>(function TitleBeautifier(
  { disabled, fetcherKey, onChange, value, className }: ITitleBeautifierProps,
  ref,
) {
  const contextPath = useContextPath();
  const [previousTitle, setPreviousTitle] = useState<string | undefined>();
  const [isTitleValid, setIsTitleValid] = useState(false);

  const [autoSubmitted, setAutoSubmitted] = useState(false);
  const [hasReset, setHasReset] = useState(false);
  const [hideTooltip, setHideTooltip] = useState(false);

  const resetTooltipRef = useClickOutside<HTMLDivElement>({
    onClickOutside: () => {
      setHideTooltip(true);
    },
  });

  const fetcher = useFetcherWithReset<typeof loader>({
    key: `title-beautifier-${fetcherKey}`,
  });
  const isPending = fetcher.state !== "idle";

  useEffect(() => {
    return () => {
      fetcher.reset();
    };
  }, []);

  useEffect(() => {
    if (fetcher.data?.title !== value) {
      setIsTitleValid(false);
    }
  }, [value]);

  useEffect(() => {
    if (fetcher.data?.title) {
      if (fetcher.data.title === value) {
        setIsTitleValid(true);
      } else {
        onChange(fetcher.data.title);
      }

      fetcher.reset();
      if (fetcher.data?.title === previousTitle) {
        setPreviousTitle(undefined);
      }
    }
  }, [fetcher.data?.title]);

  const submit = (title?: string) => {
    if (!title || title === previousTitle) return;
    setPreviousTitle(title);
    fetcher.load(contextPath("/resources/title-beautifier", { title }));
  };

  const reset = () => {
    if (!previousTitle) return;

    onChange(previousTitle);
    setPreviousTitle(undefined);
    setIsTitleValid(false);
    setHasReset(true);
    setHideTooltip(false);
  };

  if (isTitleValid) {
    return (
      <Check
        className={classNames(
          "absolute top-1/2 right-1.5 -translate-y-1/2 text-success size-4",
          className,
        )}
      />
    );
  }

  if (previousTitle && value && previousTitle !== value) {
    return (
      <Tooltip
        placement="top"
        open={!hasReset && autoSubmitted && !hideTooltip}
      >
        <TooltipTrigger asChild>
          <Button
            variant="ghost"
            icon
            onClick={reset}
            key="reset-title-beautifier"
            size="2xs"
            className={classNames(
              "!absolute top-1/2 right-1.5 -translate-y-1/2",
              className,
            )}
            disabled={disabled}
          >
            <Undo />
          </Button>
        </TooltipTrigger>
        <TooltipContent className="w-[240px]" ref={resetTooltipRef}>
          <Trans>
            The title you entered was changed by the title beautifier to fit DSP
            guidelines. If you want to revert the changes, click the undo
            button.
          </Trans>
        </TooltipContent>
      </Tooltip>
    );
  }

  return (
    <>
      <button
        type="button"
        className="hidden"
        onClick={() => {
          if (!autoSubmitted && !!value) {
            setAutoSubmitted(true);
            submit(value);
          }
        }}
        ref={ref}
      />
      <Tooltip placement="top">
        <TooltipTrigger asChild>
          <Button
            variant="ghost"
            size="2xs"
            className={classNames(
              "!absolute top-1/2 right-1.5 -translate-y-1/2",
              className,
            )}
            onClick={() => {
              submit(value);
            }}
            key="submit-title-beautifier"
            disabled={disabled}
            loading={isPending}
            icon
          >
            <Rewards />
          </Button>
        </TooltipTrigger>
        <TooltipContent className="w-[240px]">
          <Trans>
            This tool will help you verify that the title you entered fits DSP
            guidelines. If it doesn't, it will suggest a new title.
          </Trans>
        </TooltipContent>
      </Tooltip>
    </>
  );
});
